import { runningServerSide, runningClientSide } from '@benzinga/utils';
import { Subscribable } from '@benzinga/subscribable';
import { User } from '@benzinga/session';
import { MetaProps, UserSubscription, ChartbeatUserType, ConversionDestinationData } from './entities';
import { Session } from '@benzinga/session';
import { Analytics, AnalyticsBrowser } from '@segment/analytics-next';
import { TrackingEnvironment } from './environment';
import { appEnvironment, appName, getValueFromCookie } from '@benzinga/utils';
import { injectOptimizelyScript } from '@benzinga/optimizely';

declare global {
  // eslint-disable-next-line no-var
  var analytics: AnalyticsBrowser | undefined;

  interface Window {
    chartbeatLoaded?: boolean;
    loadChartbeat?: (userType: ChartbeatUserType) => void;
    _sf_async_config?: {
      authors: string;
      domain: string;
      flickerControl: boolean;
      sections: string;
      uid: number | string;
      useCanonical: boolean;
      useCanonicalDomain: boolean;
    };
    // ToDo: Cleanup section
    pSUPERFLY?: {
      virtualPage: ({
        authors,
        path,
        referrer,
        sections,
        title,
      }: {
        authors?: string | undefined;
        path?: string;
        referrer?: string | undefined;
        sections?: string;
        title?: string;
      }) => void;
    };
  }
}

interface TrackingLogMessage {
  category: string;
  message: string;
  data?: unknown;
  type: 'tracking:log_message';
}

interface VisitInfo {
  count: number;
  week: number;
  day_of_year: number;
}

export type TrackingManagerEvent = TrackingLogMessage;

export type AuthEventAction = 'login' | 'logout' | 'register' | 'forgot-password' | 'reset-password';
export type LinkEventAction = 'view' | 'hover' | 'click';
export type ButtonEventType = 'navigation' | 'page_scroll' | 'form_submit' | 'toggle' | 'go_link';

export type TrackLinkEventProperties = {
  button_id?: string;
  button_label?: string;
  button_location?: string;
  button_partner?: string;
  button_type: ButtonEventType;
  go_link_id?: string;
  link_type?: string;
  link_url?: string;
  outbound_ad_url?: string;
  paid_media_data?: ConversionDestinationData;
  recommendation_rank?: number;
  recommendation_score?: number;
  sponsor_details?: Record<string, string | number | boolean | undefined>;
};

export type CampaignEventAction = 'view' | 'click';
export type GPTEventAction = 'prompt';
export type ChatEventAction =
  | 'delete_message'
  | 'error'
  | 'flag'
  | 'mute'
  | 'unmute'
  | 'new_message'
  | 'edit_message'
  | 'click'
  | 'report'
  | 'reaction'
  | 'reply'
  | 'view_channel'
  | 'view_message';
export type CheckoutEventAction = 'click' | 'close' | 'purchase' | 'view';
export type CommentEventAction =
  | 'new_message'
  | 'reaction'
  | 'reply'
  | 'delete_message'
  | 'view_message'
  | 'open_drawer'
  | 'auth';
export type FormEventAction = 'view' | 'focus_input' | 'blur_input' | 'filled_input' | 'submit';
export type DownloadEventAction = 'view' | 'click';
export type ModalEventAction = 'view' | 'submit' | 'close';
export type NoteEventAction = 'view' | 'add' | 'delete';
export type HeadlineEventAction = 'viewed_inline' | 'clicked_inline';
export type NotificationEventAction = 'view' | 'click' | 'dismiss' | 'add' | 'remove' | 'subscribe';
export type PageEventAction = 'view' | 'scroll' | 'click' | 'load_more';
export type PaywallEventAction = 'view' | 'click' | 'login' | 'register' | 'close' | 'submit' | 'checkout';
export type ProfileEventAction = 'view' | 'edit' | 'update' | 'delete' | 'reset_password' | 'change_password';
export type SearchEventAction =
  | 'blur'
  | 'focus'
  | 'search'
  | 'result_click'
  | 'results'
  | 'filter'
  | 'cancel'
  | 'search_results';
export type SearchQueryResults = {
  article_id: string;
  relevancy_score: number;
  search_rank?: number;
  search_results_page?: number;
}[];

export type SearchEventPlatform = 'Pro' | 'Web' | 'iOS' | 'Android';

export interface TrendingResult {
  name: string;
  taxonomy: string;
  url_public: string;
}

export interface SearchEventPayload {
  platform: SearchEventPlatform;
  query?: string;
  query_results?: SearchQueryResults;
  search_type: string;
  trending_results?: TrendingResult[];
  symbol?: string;
  widget_id?: string;
  widget_type?: string;
}

export type TickerEventAction = 'view' | 'click' | 'hover';
export type HeartBeatEventAction = 'heartbeat';
export type WnstnEventAction =
  | 'follow_up_wnstn_widget_click'
  | 'follow_up_wnstn_widget_view'
  | 'asset_wnstn_widget_view'
  | 'asset_wnstn_widget_click'
  | 'wnstn_floating_widget_hover'
  | 'wnstn_floating_widget_view';
export type UserLocationEventAction = 'detect' | 'update';
export type TableEventAction =
  | 'view'
  | 'row_click'
  | 'sort'
  | 'filter'
  | 'load_more'
  | 'change_page'
  | 'change_page_size';
export type VideoEventAction =
  | 'view'
  | 'play'
  | 'pause'
  | 'seek'
  | 'end'
  | 'mute'
  | 'unmute'
  | 'fullscreen'
  | 'exit_fullscreen'
  | 'volume_change';
export type SquawkWidgetEventAction =
  | 'connected'
  | 'connection_error'
  | 'stream_error'
  | 'disconnected'
  | 'mute'
  | 'unmute'
  | 'volume_up'
  | 'volume_down';
export type WidgetEventAction =
  | 'view'
  | 'click'
  | 'load_more'
  | 'save_export'
  | 'refresh_data'
  | 'error'
  | 'status'
  | 'add_filter'
  | 'remove_filter'
  | 'update_setting'
  | 'open_upcoming_earnings'
  | 'change_calendar_type'
  | 'open_default_calendar_type'
  | 'sort'
  | 'add_to_workspace'
  | 'popout'
  | 'delete_from_workspace'
  | 'close'
  | SquawkWidgetEventAction;
export type WatchlistEventAction =
  | 'view'
  | 'view_all'
  | 'new_watchlist'
  | 'delete_watchlist'
  | 'add_symbol'
  | 'delete_symbol'
  | 'update_symbol'
  | 'add_note'
  | 'enable_alert'
  | 'disable_alert'
  | 'import'
  | 'delete_note';
export type SettingsEventAction =
  | 'view'
  | 'update'
  | 'delete'
  | 'add'
  | 'enable'
  | 'disable'
  | 'change'
  | 'reset'
  | 'select'
  | 'deselect';
export type OnboardingEventAction =
  | 'view'
  | 'click'
  | 'submit'
  | 'close'
  | 'next'
  | 'back'
  | 'complete_step'
  | 'skip'
  | 'start'
  | 'complete_all';
export type ErrorEventAction = 'emit';
export type LayoutEventAction =
  | 'view'
  | 'error'
  | 'load_workspace'
  | 'load_workspace_preset'
  | 'add_workspace'
  | 'delete_workspace'
  | 'update_workspace'
  | 'add_to_workspace'
  | 'delete_from_workspace'
  | 'load_layout'
  | 'add_layout'
  | 'reset_layout'
  | 'delete_layout'
  | 'update_layout'
  | 'add_to_layout'
  | 'delete_from_layout'
  | 'pane_resize';
export type ChartEventAction =
  | 'interval_changed'
  | 'visible_range_changed'
  | 'select_layout_click'
  | 'save_layout_click'
  | 'rename_layout_click'
  | 'new_layout_click'
  | 'select_drawing_click'
  | 'rename_drawing_click'
  | 'new_drawing_click'
  | 'copy_drawing_click'
  | 'select_local_click'
  | 'merge_drawing_click'
  | 'delete_drawing_click'
  | 'delete_local_click'
  | 'delete_all_click'
  | 'delete_all_local_click'
  | 'delete_all_local_click';

export type SubscriptionEventAction = 'added' | 'removed' | 'updated' | 'cancelled' | 'renewed' | 'paused';

export type AuthEventProperties = { [key: string]: any };

export type AnyEventAction =
  | AuthEventAction
  | LinkEventAction
  | CampaignEventAction
  | ChatEventAction
  | CheckoutEventAction
  | CommentEventAction
  | DownloadEventAction
  | ErrorEventAction
  | FormEventAction
  | GPTEventAction
  | HeartBeatEventAction
  | LayoutEventAction
  | ModalEventAction
  | NotificationEventAction
  | OnboardingEventAction
  | PageEventAction
  | PaywallEventAction
  | ProfileEventAction
  | SearchEventAction
  | SettingsEventAction
  | SubscriptionEventAction
  | TableEventAction
  | TickerEventAction
  | UserLocationEventAction
  | VideoEventAction
  | WatchlistEventAction
  | WidgetEventAction
  | WnstnEventAction
  | HeadlineEventAction
  | ChartEventAction
  | RecommendationEventAction;

export type AuthEventType = 'email' | 'google' | 'apple' | 'microsoft' | 'google-one-tap' | 'social-fallback';

export type RecommendationEventAction = 'recommendation_results';

export const GA4_SESSION_COOKIE = '_ga_ZVH0G4BQKE'; // _ga_{property_id}

export class TrackingManager extends Subscribable<TrackingManagerEvent> {
  protected meta = {} as MetaProps | null;
  protected subscriptions = [] as UserSubscription[] | null | undefined;
  protected user = { email: 'unknown' } as User | null | undefined;
  private warnedNoIntercom = false;
  private session: Session;
  private uniqueId: number | null = null;
  private context = { isIncognito: null, isPaywalled: false, isRegwalled: false } as {
    isIncognito: null | boolean;
    isPaywalled: boolean;
    isRegwalled: boolean;
  };
  private logEnabled = false;
  private localStorageKey = 'pwdata';
  private platform: SearchEventPlatform;

  constructor(session: Session) {
    super();
    this.uniqueId = new Date().getTime() + Math.floor(Math.random() * 1000000);
    this.session = session;
    this.platform = 'Web';
    if (runningClientSide()) {
      if (session.getEnvironment(TrackingEnvironment).segmentKey) {
        const writeKey = session.getEnvironment(TrackingEnvironment).segmentKey;
        const existingGlobalAnalytics = globalThis.analytics;
        const loadPromise =
          !existingGlobalAnalytics || typeof existingGlobalAnalytics.user !== 'function'
            ? (globalThis.analytics = AnalyticsBrowser.load({ writeKey }))
            : null;
        (async () => {
          try {
            const thenable = loadPromise as PromiseLike<[Analytics, unknown]>;
            const resolved = loadPromise ? await thenable : null;
            // if (loadPromise && resolved) {
            //   const contextKeys =
            //     resolved[1] && typeof resolved[1] === 'object' ? Object.keys(resolved[1] as object) : undefined;
            //   console.log('[tracking] Segment load() returned:', {
            //     contextKeys,
            //     hasAnalytics: !!resolved[0],
            //     hasContext: !!resolved[1],
            //     isTuple: Array.isArray(resolved),
            //     length: resolved.length,
            //   });
            // } else if (!loadPromise) {
            //   console.log('[tracking] Segment load() skipped (using existing global analytics)');
            // }
            const analytics: Analytics | AnalyticsBrowser | null = loadPromise
              ? (resolved?.[0] ?? null)
              : (existingGlobalAnalytics ?? null);
            const user = await analytics?.user?.();
            if (!user) return;
            const anonymousId = user.anonymousId?.();
            const userId = user.id?.();
            if (userId || anonymousId) {
              const isBzApp = appEnvironment().isApp(appName.bz);
              const isMoneyApp = appEnvironment().isApp(appName.money);
              if (isBzApp || isMoneyApp) injectOptimizelyScript();
            }
          } catch {
            // Avoid unhandled rejection if load() or .user() fails
          }
        })();
      }
    }
  }

  public static getName = () => 'benzinga-tracking';

  public updateContext(context: { isPaywalled?: boolean; isIncognito?: boolean | null; isRegwalled?: boolean }) {
    this.context = { ...this.context, ...context };
  }

  public setMeta(metaData: MetaProps | null) {
    this.meta = metaData;
  }

  /** Set platform for search (and other) events: Pro | Web | iOS | Android. Overrides default. */
  public setPlatform(platform: SearchEventPlatform) {
    this.platform = platform;
  }

  public setUser(user: User | null | undefined) {
    this.user = user;
  }

  public setSubscriptions(subscriptions: UserSubscription[] | null | undefined) {
    this.subscriptions = subscriptions;
  }

  public identify(userId: number, properties: any) {
    // Segment
    if (globalThis.analytics) {
      globalThis.analytics.identify(userId ? String(userId) : null, properties);
    }

    // Google Tag Manager
    if ((window as any).gtagAnalytics) {
      (window as any).gtagAnalytics.identify(userId, properties);
    }
  }

  public setLogging(logEnabled: boolean) {
    this.logEnabled = logEnabled;
  }

  public logout() {
    if (runningServerSide()) return;
    (window as any).analytics.reset();
  }

  /**
   * Tracks auth events (login/register). Returns a Promise so callers can await before
   * redirecting; uses doSegmentTrack directly (no requestIdleCallback) to avoid the
   * redirect firing before the event is sent.
   */
  public async trackAuthEvent(
    action: AuthEventAction,
    properties: { auth_type?: AuthEventType; is_paywalled?: boolean; is_regwalled?: boolean },
  ): Promise<void> {
    const { auth_type, is_paywalled, is_regwalled } = properties;
    await this.doSegmentTrack('auth_event', action, {
      auth_type,
      is_paywalled: is_paywalled ?? this.context.isPaywalled,
      is_regwalled: is_regwalled ?? this.context.isRegwalled,
    });
    // Brief delay so Segment has time to flush before page unload (e.g. redirect).
    if (runningClientSide()) {
      await new Promise(resolve => setTimeout(resolve, 150));
    }
  }

  public trackPageEvent(
    action: PageEventAction,
    properties: { page_tab?: string; page_section?: string; scroll_depth?: number },
  ) {
    const { page_section, page_tab } = properties;
    this.segmentTrack('page_event', action, { is_incognito: this.context.isIncognito, page_section, page_tab });
  }

  public trackTableEvent(
    action: TableEventAction,
    table_id: string,
    table_type: string,
    properties: { page_num?: number; page_size?: number },
  ) {
    const { page_num, page_size } = properties;
    this.segmentTrack('table_event', action, {
      page_num, // the current page number
      page_size, // the number of rows per page
      table_id, // the id of the table
      table_type, // the type of table (e.g. earnings, ratings)
    });
  }

  public trackDownloadEvent(action: DownloadEventAction, properties: { asset_id?: string; asset_url?: string }) {
    const { asset_id, asset_url } = properties;
    this.segmentTrack('download_event', action, {
      asset_id, // the id of the asset
      asset_url, // the url of the asset
    });
  }

  public trackHeadlineEvent(action: HeadlineEventAction, properties: { nodeID: string }) {
    const { nodeID } = properties;
    this.segmentTrack('page_event', action, { node_id: nodeID });
  }

  public trackCampaignEvent(
    action: CampaignEventAction,
    properties: {
      campaign_id?: string;
      destination_url?: string;
      label?: string;
      non_interaction?: boolean;
      outbound_ad_url?: string;
      partner_id?: string;
      sponsored?: string;
      tag?: string;
      unit_type?: string;
      url?: string;
      utmParams?: { ad?: string; adType?: string; campaign?: string };
    },
  ) {
    const {
      campaign_id,
      destination_url,
      label,
      non_interaction,
      partner_id,
      sponsored,
      tag,
      unit_type,
      url,
      utmParams,
    } = properties;
    this.segmentTrack('campaign_event', action, {
      campaign_id, // the id of the campaign
      destination_url, // the destination url of the campaign
      label, // the label for this campaign
      non_interaction,
      partner_id, // the partner id for this campaign, use benzinga if internal
      sponsored, // whether the campaign is sponsored
      tag, // the tag for this campaign
      unit_type, // the type of unit (e.g. com-top-banner, campaignify-top, etc)
      url, // the url of the campaign
      ...(utmParams || {}),
    });
  }

  public trackUserLocationEvent(action: UserLocationEventAction, properties: { timezone?: string }) {
    const { timezone } = properties;
    this.segmentTrack('user_location_event', action, {
      timezone, // user's timezone
    });
  }

  public trackCheckoutEvent(
    action: CheckoutEventAction,
    properties: {
      amount?: number;
      checkout_mode?: string;
      checkout_session_id?: string;
      checkout_type?: string;
      duration_ms?: number;
      package_id?: string;
      page_url?: string;
      placement?: string;
      product_id?: string;
      utm_ad?: string;
      utm_ad_type?: string;
      utm_campaign?: string;
    },
  ) {
    const {
      amount,
      checkout_mode,
      checkout_session_id,
      checkout_type,
      duration_ms,
      package_id,
      page_url,
      placement,
      product_id,
      utm_ad,
      utm_ad_type,
      utm_campaign,
    } = properties;
    this.segmentTrack('checkout_event', action, {
      amount, // purchase amount in dollars
      checkout_mode, // modal or new_tab
      checkout_session_id, // ties click/view/purchase/close into one funnel
      checkout_type, // the type of checkout (e.g. clickfunnels, cartflow, etc)
      duration_ms, // time spent in checkout before purchase or close
      package_id, // the package id being purchased
      page_url, // the page url where checkout was opened
      placement, // where on the page checkout was triggered
      product_id, // the product id being purchased
      utm_ad, // utm ad from checkout params
      utm_ad_type, // utm ad type from checkout params
      utm_campaign, // utm campaign from checkout params
    });
  }

  public trackWnstnEvent(
    action: WnstnEventAction,
    properties: { node_id?: string; question?: string; page_link?: string; symbol?: string },
  ) {
    const { node_id, page_link, question, symbol } = properties;
    this.segmentTrack('wnstn_widget', action, {
      node_id, // article node id if placed on article page
      page_link, // page link
      question, // selected question from wnstn widget
      symbol,
    });
  }

  public trackRecommendedEvent(
    action: RecommendationEventAction,
    properties: {
      recommended_results: { article_id: number; score: number; rank: number }[];
      source_article_id: string;
    },
  ) {
    const { recommended_results, source_article_id } = properties;
    this.segmentTrack('recommendation_event', action, {
      recommended_results,
      source_article_id,
    });
  }

  public trackSearchEvent(
    action: SearchEventAction,
    search_type: string,
    properties: {
      query?: string;
      symbol?: string;
      widget_id?: string;
      widget_type?: string;
      query_results?: SearchQueryResults;
      trending_results?: TrendingResult[];
    },
  ) {
    const { query, query_results, trending_results, widget_id, widget_type } = properties;
    const payload: SearchEventPayload = {
      platform: this.platform,
      query,
      query_results,
      search_type,
      trending_results,
      widget_id,
      widget_type,
    };
    this.segmentTrack('search_event', action, payload);
  }

  public trackNoteEvent(action: NoteEventAction, properties: { symbol: string }) {
    const { symbol } = properties;
    this.segmentTrack('note_event', action, {
      symbol, // the symbol being noted
    });
  }

  public trackLinkEvent(action: LinkEventAction, properties: TrackLinkEventProperties) {
    const {
      button_id,
      button_label,
      button_location,
      button_partner,
      button_type,
      go_link_id,
      link_type,
      link_url,
      outbound_ad_url,
      paid_media_data,
      recommendation_rank,
      recommendation_score,
      sponsor_details,
    } = properties;
    this.segmentTrack('button_event', action, {
      button_id,
      button_label,
      button_location,
      button_partner,
      button_type,
      go_link_id,
      link_type,
      link_url,
      outbound_ad_url,
      paid_media_data,
      recommendation_rank,
      recommendation_score,
      sponsor_details,
    });
  }

  public trackChatEvent(
    action: ChatEventAction,
    properties: {
      button_id?: string;
      button_value?: string;
      channel_id?: string;
      message_id?: string;
      quoted_id?: string;
      thread_id?: string;
      product_id?: string;
      reaction?: string;
      muted_user_id?: string;
      report_message?: string;
      user_id?: string;
      error_message?: string;
    },
  ) {
    const {
      button_id,
      button_value,
      channel_id,
      error_message,
      message_id,
      muted_user_id,
      product_id,
      quoted_id,
      reaction,
      report_message,
      thread_id,
      user_id,
    } = properties;
    this.segmentTrack('chat_event', action, {
      button_id, // the id of the button
      button_value, // the text/icon on the button
      channel_id, // channel id where chat is occuring
      error_message, // error message if any
      message_id, // the id of the message
      muted_user_id, // the user id being muted
      product_id, // which product is the chat occuring under
      quoted_id, // the id of the quoted message
      reaction, // the reaction being added
      report_message, // the type of report (e.g. spam, inappropriate, etc)
      thread_id, // the id of the thread
      user_id: user_id, // chat id for user
    });
  }

  public trackCommentEvent(
    action: CommentEventAction,
    properties: { page_type?: string; page_tab?: string; page_section?: string },
  ) {
    const { page_section, page_tab, page_type } = properties;
    this.segmentTrack('comment_event', action, { page_section, page_tab, page_type });
  }

  public trackNotificationEvent(
    action: NotificationEventAction,
    properties: { notification_type?: string; notification_id?: string },
  ) {
    const { notification_id, notification_type } = properties;
    this.segmentTrack('notification_event', action, {
      notification_id, // the id of the notification
      notification_type, // the type of notification (e.g. watchlist_alert, promo, etc)
    });
  }

  public trackProfileEvent(action: ProfileEventAction) {
    this.segmentTrack('profile_event', action, {});
  }

  public trackWatchlistEvent(action: WatchlistEventAction, properties: { symbol?: string; watchlist_id?: string }) {
    const { symbol, watchlist_id } = properties;
    this.segmentTrack('watchlist_event', action, {
      symbol, // the symbol being modified
      watchlist_id, // the watchlist id
    });
  }

  public trackTickerEvent(action: TickerEventAction, properties: { symbol?: string; source?: string }) {
    const { source, symbol } = properties;
    this.segmentTrack('ticker_event', action, {
      source, // the source of the ticker event (e.g. watchlist, article, etc)
      ticker: symbol, // the symbol being interacted with
    });
  }

  public trackModalEvent(
    action: ModalEventAction,
    properties: { modal_id?: string; allow_close?: boolean; modal_type?: string },
  ) {
    const { allow_close, modal_id, modal_type } = properties;
    this.segmentTrack('modal_event', action, {
      allow_close, // whether the modal can be closed
      modal_id, // the id of the modal
      modal_type, // the type of modal (e.g. ok_cancel, prompt, etc)
    });
  }

  public trackVideoEvent(
    action: VideoEventAction,
    properties: { partner_id?: string; video_id?: string; video_type?: string },
  ) {
    const { partner_id, video_id, video_type } = properties;
    this.segmentTrack('video_event', action, {
      partner_id, // the partner id for this video, use benzinga if internal
      video_id, // the id of the video
      video_type, // the type of video (e.g. youtube, vimeo, etc)
    });
  }

  public trackPaywallEvent(
    action: PaywallEventAction,
    properties: {
      paywall_id?: string;
      paywall_type?: string;
      placement?: string;
      utmParams?: { adType?: string; ad?: string; campaign?: string };
    },
  ) {
    const { paywall_id, paywall_type, placement, utmParams } = properties;
    this.segmentTrack('paywall_event', action, { paywall_id, paywall_type, placement, ...(utmParams || {}) });
  }

  public trackFormEvent(
    action: FormEventAction,
    form_id: string,
    properties: { form_id?: string; form_type?: string; email?: string },
  ) {
    const { email, form_type } = properties;
    this.segmentTrack('form_event', action, { email, form_id, form_type });
  }

  public trackChartEvent(
    action: ChartEventAction,
    properties: {
      symbol?: string;
      timeframe?: string;
      widget_id?: string;
      widget_type?: string;
      chart_layout_id?: string;
    },
  ) {
    const { chart_layout_id, symbol, timeframe, widget_id, widget_type } = properties;
    this.segmentTrack('chart_event', action, { chart_layout_id, symbol, timeframe, widget_id, widget_type });
  }

  public trackWidgetEvent(
    action: WidgetEventAction,
    widget_type?: string,
    properties?: {
      asset_type?: string;
      error_message?: string;
      period?: string;
      symbol?: string;
      filter_id?: string;
      filter_value?: string;
      setting_id?: string;
      setting_value?: string;
      button_id?: string;
      button_value?: string;
      widget_sub_type?: string;
      widget_sub_type_value?: string | boolean;
      widget_preset?: string;
      widget_filter?: any;
      widget_id?: string;
      layout_id?: string;
    },
  ) {
    const {
      asset_type,
      button_id,
      button_value,
      filter_id,
      filter_value,
      layout_id,
      period,
      setting_value,
      symbol,
      widget_filter,
      widget_id,
      widget_preset,
      widget_sub_type,
      widget_sub_type_value,
    } = properties || {};
    this.segmentTrack('widget_event', action, {
      asset_type,
      button_id,
      button_value,
      filter_id,
      filter_value,
      layout_id,
      period,
      setting_value,
      symbol,
      widget_filter,
      widget_id,
      widget_preset,
      widget_sub_type,
      widget_sub_type_value,
      widget_type,
    });
  }

  public trackLayoutEvent(
    action: LayoutEventAction,
    properties: {
      error_message?: string;
      layout_id?: string;
      layout_type?: string;
      preset?: string;
      widget_id?: string;
      widget_type?: string;
      workspace_count?: number;
      workspace_id?: string;
      property_change?: string;
    },
  ) {
    const { error_message, layout_id, layout_type, preset, widget_id, widget_type, workspace_count, workspace_id } =
      properties;
    this.segmentTrack('layout_event', action, {
      error_message,
      layout_id,
      layout_type,
      preset,
      widget_id,
      widget_type,
      workspace_count,
      workspace_id,
    });
  }

  public trackSettingsEvent(
    action: SettingsEventAction,
    properties: { setting_id?: string; setting_type?: string; setting_value?: string },
  ) {
    const { setting_id, setting_type, setting_value } = properties;
    this.segmentTrack('settings_event', action, { setting_id, setting_type, setting_value });
  }

  public trackSubscriptionEvent(
    action: SubscriptionEventAction,
    properties: { interval?: string; package_id?: string; product_id?: string; price: number; reason?: string },
  ) {
    const { interval, package_id, price, product_id, reason } = properties;
    this.segmentTrack('subscription_event', action, { interval, package_id, price, product_id, reason });
  }

  public trackErrorEvent(action: ErrorEventAction, properties: { error_message?: string; user_id?: string }) {
    const { error_message, user_id } = properties;
    this.segmentTrack('error_event', action, { error_message, user_id });
  }

  public trackGPTEvent(
    action: GPTEventAction,
    properties: { prompt?: string; prompt_author?: string; thread_id?: string; reply?: string; reply_author?: string },
  ) {
    const { prompt, prompt_author, reply, reply_author, thread_id } = properties;
    this.segmentTrack('gpt_event', action, { prompt, prompt_author, reply, reply_author, thread_id });
  }

  public trackHeartBeatEvent(action: HeartBeatEventAction, properties: { layout_id?: string }) {
    this.segmentTrack('heartbeat_event', action, properties);
  }

  public trackOnboardingEvent(
    action: OnboardingEventAction,
    properties: { onboarding_step?: string; onboarding_step_value?: string },
  ) {
    const { onboarding_step, onboarding_step_value } = properties;
    this.segmentTrack('onboarding_event', action, { onboarding_step, onboarding_step_value });
  }

  // Update the visit count and current week in local storage
  protected updateVisitInfo(): void {
    const dayNow = new Date();
    const start = new Date(dayNow.getFullYear(), 0, 0);
    const diff = dayNow.getTime() - start.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    const dayOfYear = Math.floor(diff / oneDay);

    let userVisitInfo: VisitInfo = { count: 1, day_of_year: dayOfYear, week: this.getWeekNumber(new Date(), 3) };

    try {
      const lastVisitInfo = JSON.parse(localStorage.getItem(this.localStorageKey) || '{}') as VisitInfo;
      // if (lastVisitInfo && lastVisitInfo.day_of_year === userVisitInfo.day_of_year) {
      if (lastVisitInfo && lastVisitInfo.week === userVisitInfo.week) {
        lastVisitInfo.count += 1;
        userVisitInfo = lastVisitInfo;
      }
    } catch (e) {
      console.error(e);
    }
    localStorage.setItem(this.localStorageKey, JSON.stringify(userVisitInfo));
  }

  protected getArticleTemplateType = (): 'new' | 'old' => {
    const override = new URLSearchParams(window.location.search).get('template') as 'new' | 'old';
    if (override && ['new', 'old'].includes(override)) return override;
    return 'new';
  };

  protected getValueFromCookie = (key: string): string | undefined => {
    if (typeof document !== 'undefined') {
      return document.cookie
        .split('; ')
        .find(cookie => cookie.startsWith(key))
        ?.split('=')[1];
    }
    return undefined;
  };

  protected getUrlParameter = (param: string): string | null => {
    if (typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(param);
    }
    return null;
  };

  protected doSegmentTrack = async (event: string, action: AnyEventAction, properties: { [key: string]: any }) => {
    if (runningServerSide()) return;

    // TODO: Testing Sophi
    const sophiData = window?.['sophi']?.dataLayer;

    const props = {
      action: action,
      article_section: this.meta?.structuredData?.articleSection || undefined,
      category: event + '__' + action,
      client_id: getValueFromCookie('_ga') || undefined,
      context_ip: undefined, // TODO: Get from server-side or IP detection service
      context_locale: navigator.language,
      context_page_path: window.location.pathname,
      context_page_referrer: this.meta?.referer || document.referrer || undefined,
      context_page_sub_title: this.meta?.subTitle || undefined,
      context_page_title: this.meta?.title || undefined,
      context_page_url: window.location.href,
      context_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      context_user_agent: navigator.userAgent,
      context_utm_ad: this.getUrlParameter('utm_ad') || undefined,
      context_utm_ad_type: this.getUrlParameter('utm_ad_type') || undefined,
      context_utm_campaign: this.getUrlParameter('utm_campaign') || undefined,
      context_utm_medium: this.getUrlParameter('utm_medium') || undefined,
      context_utm_source: this.getUrlParameter('utm_source') || undefined,
      event: event,
      event_data: {
        outbound_ad_url: properties['action'] === 'click' ? properties['outbound_ad_url'] : undefined,
        page_type: this.meta?.pageType || undefined,
        release_version: process.env['RELEASE_VERSION'] || undefined,
        user_logged_in: this.user?.accessType && this.user.accessType !== 'anonymous' ? true : false,
        vertical: Array.isArray(this.meta?.vertical)
          ? this.meta.vertical
              ?.map(v => {
                return v.toString().replaceAll('"', '');
              })
              .join(', ')
          : undefined,
        ...properties,
      },
      iam_uuid: this.user && this.user.uuid,
      sophi_context: sophiData?.context,
      sophi_experiments_code: sophiData?.experimentsCode,
      sophi_inputs: sophiData?.inputs,
      sophi_trace: sophiData?.trace,
      sophi_user_properties: sophiData?.userProperties,
    };

    if (globalThis.analytics) {
      if (event === 'page_event' && action === 'view') {
        if (this.meta?.nodeId) {
          // we only want to count the article views to increment actions
          this.updateVisitInfo();
        }

        // Google Tag Manager
        if ((window as any).gtagAnalytics) {
          // page_url must be the actual URL, not the page title. Passing the title here
          // is what pushed GA4 pageviews into "Page Not Found"/homepage buckets and
          // widened the GA4↔Segment delta (BZFE-15742).
          (window as any).gtagAnalytics.trackPage(props.context_page_url || props.context_page_path || event, props);
        }
        // Send page event for use in Segment elsewhere - We'll convert the below page_event__view to a page event in GA4
        globalThis.analytics.page(this.meta?.pageType, props.context_page_title, props);
      }
      globalThis.analytics.track(event, props);

      if (event === 'page_event' && action === 'view') {
        if (!window?.chartbeatLoaded) {
          if (window?.loadChartbeat && document.getElementById('chartbeat-config')) {
            window.chartbeatLoaded = true;
            const userType: ChartbeatUserType =
              this.user?.accessType === 'subscribed'
                ? 'paid'
                : this.user?.accessType === 'anonymous'
                  ? 'anon'
                  : 'lgdin';
            window.loadChartbeat(userType);
          }
        } else if (window?.pSUPERFLY && !appEnvironment().isApp(appName.bz)) {
          // BZ App Router owns Chartbeat SPA updates via ChartbeatAnalytics (resolveChartbeatMeta).
          // Money still uses this path (_document Chartbeat has no virtualPage on route change).
          // Revisit the !isBzApp guard when Money migrates to App Router and uses the same component.
          const channels =
            this.meta?.dimensions?.channels?.map(channel => channel.toString().replaceAll('"', '')).join(', ') || '';
          let chartbeatSections = channels.toString() || '';
          const isMoneyApp = appEnvironment().isApp(appName.money);

          if (isMoneyApp) {
            chartbeatSections = this.meta?.vertical?.map(v => v.toString().replaceAll('"', '')).join(', ') || '';
          }

          window.pSUPERFLY.virtualPage({
            authors: this.meta?.dimensions?.authorName || this.meta?.author || 'Benzinga',
            path: window.location.pathname,
            sections: chartbeatSections,
            title: props?.context_page_title ?? document?.title ?? '',
          });
        }
      }
    }

    this.dispatch({
      category: event + '__' + action,
      data: props,
      message: globalThis.analytics ? 'Event sent to Segment' : 'Event not sent to Segment',
      type: 'tracking:log_message',
    });
  };

  protected segmentTrack = (event: string, action: AnyEventAction, properties: { [key: string]: any }) => {
    // run tracking on the callback queue to allow for react to finish passing along page data on the microtask queue
    if (
      Object.prototype.hasOwnProperty.call(window, 'requestIdleCallback') &&
      typeof window.requestIdleCallback === 'function'
    ) {
      requestIdleCallback(() => {
        this.doSegmentTrack(event, action, properties);
      });
    } else {
      setTimeout(() => {
        this.doSegmentTrack(event, action, properties);
      }, 0);
    }
  };

  // Function to get the week number of the year
  private getWeekNumber(date: Date, startOfWeek: number): number {
    // startOfWeek should be a number from 0 to 6:
    // 0 - Sunday
    // 1 - Monday
    // 2 - Tuesday
    // 3 - Wednesday
    // 4 - Thursday
    // 5 - Friday
    // 6 - Saturday

    const oneJan = new Date(date.getFullYear(), 0, 1);
    // Adjust oneJan.getDay() to make the specified start day of the week the start of the week
    const dayOfWeek = (oneJan.getDay() - startOfWeek + 7) % 7; // Shifts the week start to the specified day
    return Math.ceil(((date.getTime() - oneJan.getTime()) / 86400000 + dayOfWeek) / 7);
  }

  private log(message: any) {
    if (this.logEnabled) {
      // tslint:disable-next-line:no-console
      console.log('TrackingManager:', message);
    }
  }
}
